import {DashboardCard, GenericInput, H3, Icon, TextRegular} from '@hm/fundamental'
import {mdiChevronDown, mdiChevronRight, mdiMagnify, mdiMagnifyRemoveOutline} from '@mdi/js'
import {useDebounce} from '@react-hook/debounce'
import {useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {ExtendedGlobalMeasurementTypesCategory} from '../types/GlobalMeasurementTypesCategory'
import {LimsMeasurementType} from '../types/LimsMeasurementType'

import {LimsMeasurementTypesForm} from './LimsMeasurementTypesForm'
import {useManagePinningRows} from './LimsMeasurementTypesForm/hooks'

import {usePermission} from '@settings/modules/permissions'
import {useUrlParam} from '@settings/modules/routing'

const DEBOUNCE_DELAY = 300

interface MeasurementTypesCardProps {
  selectedMeasurementTypesCategory: ExtendedGlobalMeasurementTypesCategory
  unmappedLimsMeasurementTypes: LimsMeasurementType[]
}

export function MeasurementTypesCard({
  selectedMeasurementTypesCategory,
  unmappedLimsMeasurementTypes
}: MeasurementTypesCardProps): JSX.Element {
  const plantCode = useUrlParam('plantCode')
  const selectedCategoryId = String(selectedMeasurementTypesCategory.id)
  const canChangeMappings = usePermission('CHANGE_MATERIALS')
  const [searchQuery, setSearchQuery] = useDebounce('', DEBOUNCE_DELAY)
  const [isPinnedSectionExpanded, setIsPinnedSectionExpanded] = useState(true)
  const {
    selectedCategoryPinnedIds,
    handlePinGlobalMeasurementType,
    isPinGlobalMeasurementNamePending
  } = useManagePinningRows({plantCode, selectedCategoryId})
  const {t} = useTranslation()

  const filteredGlobalTypes = useMemo(() => {
    const query = searchQuery.toLowerCase().trim()
    if (!query) {
      return selectedMeasurementTypesCategory.globalMeasurementTypes
    }
    return selectedMeasurementTypesCategory.globalMeasurementTypes.filter((globalType) =>
      globalType.name.toLowerCase().includes(query)
    )
  }, [searchQuery, selectedMeasurementTypesCategory.globalMeasurementTypes])

  const pinnedGlobalTypes = useMemo(
    () => filteredGlobalTypes.filter((globalType) => selectedCategoryPinnedIds.has(globalType.id)),
    [filteredGlobalTypes, selectedCategoryPinnedIds]
  )

  const unpinnedGlobalTypes = useMemo(
    () => filteredGlobalTypes.filter((globalType) => !selectedCategoryPinnedIds.has(globalType.id)),
    [filteredGlobalTypes, selectedCategoryPinnedIds]
  )

  return (
    <DashboardCard data-test-id="measurements_types_card" className="p-4">
      <div className="mb-4 pb-6">
        <GenericInput
          name="measurement_types_search"
          placeholder={t('measurementsSettings.searchPlaceholder')}
          onChange={setSearchQuery}
          className="w-full [&>div]:min-h-12 [&>div]:items-center [&_input]:h-8 [&_input]:mb-0 [&_input]:mt-0"
          data-test-id="measurement_types_search_input"
          data-cy="measurement_types_search_input"
          icon={mdiMagnify}
        />
      </div>
      {filteredGlobalTypes.length === 0 ? (
        <div
          className="flex flex-col items-center gap-2 py-8"
          data-test-id="measurement_types_no_results"
        >
          <Icon path={mdiMagnifyRemoveOutline} size="large" className="text-foreground_faded" />
          <TextRegular className="text-foreground_faded">
            {t('measurementsSettings.noResults', {searchQuery})}
          </TextRegular>
        </div>
      ) : (
        <div>
          {pinnedGlobalTypes.length > 0 && (
            <div className="pb-6" data-test-id="measurement_types_pinned_section">
              <button
                type="button"
                className="flex w-full items-center justify-start gap-2 bg-transparent px-0 py-0 text-left pb-3"
                aria-expanded={isPinnedSectionExpanded}
                aria-controls="measurement-types-pinned-content"
                onClick={() => setIsPinnedSectionExpanded((prev) => !prev)}
              >
                <Icon
                  size="large"
                  path={isPinnedSectionExpanded ? mdiChevronDown : mdiChevronRight}
                  data-cy="measurement_types_pinned_expand_icon"
                />
                <H3>{t('measurementsSettings.pinned')}</H3>
              </button>
              {isPinnedSectionExpanded && (
                <div className="mt-2 flex flex-col gap-6" id="measurement-types-pinned-content">
                  {pinnedGlobalTypes.map((globalType) => (
                    <LimsMeasurementTypesForm
                      key={globalType.id}
                      globalMeasurementType={globalType}
                      unmappedLimsMeasurementTypes={unmappedLimsMeasurementTypes}
                      canChangeMappings={canChangeMappings}
                      plantCode={plantCode}
                      onPinGlobalMeasurementType={handlePinGlobalMeasurementType}
                      isPinned
                      isPinPending={isPinGlobalMeasurementNamePending}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="flex flex-col gap-6">
            <H3>{t('measurementsSettings.allMeasurements')}</H3>
            {unpinnedGlobalTypes.map((globalType) => (
              <LimsMeasurementTypesForm
                key={globalType.id}
                globalMeasurementType={globalType}
                unmappedLimsMeasurementTypes={unmappedLimsMeasurementTypes}
                canChangeMappings={canChangeMappings}
                plantCode={plantCode}
                onPinGlobalMeasurementType={handlePinGlobalMeasurementType}
                isPinned={false}
                isPinPending={isPinGlobalMeasurementNamePending}
              />
            ))}
          </div>
        </div>
      )}
    </DashboardCard>
  )
}
